"use client";

import { ReactNode } from "react";
import { usePathname } from "next/navigation";
import FloatingNavbar from "@/features/dashboard/components/FloatingNavbar";
import { DemoBanner } from "@/components/demo/DemoBanner";
import dynamic from "next/dynamic";
import { UploadCompleteListener } from "@/features/wardrobe/components/UploadCompleteListener";

// A 600-line popup pulling framer-motion, shipped in the boot chunk of every
// /user/* route just to check whether there's an unseen tarot card. It's already
// render-gated below; loading it lazily keeps it off the critical path.
const TarotLootPopup = dynamic(() => import("@/shared/components/layout/TarotLootPopup"), {
  ssr: false,
});

const NAV_BLACKLIST = [
  "/user/onboarding",
  "/login",
  "/demo",
  "/",
  "/about",
  "/admin-page",
  "/blog",
  "/legal",
  "/support",
  "/waitlist",
  "/reset-password",
];

const DEMO_BANNER_BLACKLIST = [
  "/",
  "/login",
  "/demo",
  "/about",
  "/admin-page",
  "/blog",
  "/legal",
  "/support",
  "/waitlist",
  "/reset-password",
  "/user/onboarding",
];

const matchesRoute = (pathname: string, route: string) => {
  if (route === "/") {
    return pathname === "/";
  }
  return pathname === route || pathname.startsWith(`${route}/`);
};

const shouldHideNavbar = (pathname: string) =>
  NAV_BLACKLIST.some((route) => matchesRoute(pathname, route));

export default function AppShell({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  const resolvedPath = pathname ?? "/";
  const hideNavbar = shouldHideNavbar(resolvedPath);
  const hideDemoBanner = DEMO_BANNER_BLACKLIST.some((route) => matchesRoute(resolvedPath, route));

  const showTarotLoot = resolvedPath.startsWith("/user/");

  return (
    <div className="flex min-h-full flex-col">
      {!hideDemoBanner && <DemoBanner />}
      {!hideNavbar && <FloatingNavbar />}
      <main id="main-content" className="flex-1 overflow-y-auto">{children}</main>
      {showTarotLoot && <TarotLootPopup />}
      <UploadCompleteListener />
    </div>
  );
}
